//
// Copyright 2024 DXOS.org
//

import { randSentence, randWord } from '@ngneat/falso'; // TODO(burdon): Reconcile with echo-generator.
import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';

import {
  DatabaseCard,
  Devtools,
  MemoryCard,
  NetworkCard,
  PerformanceCard,
  QueriesCard,
  ReplicatorCard,
  ReplicatorMessagesCard,
  StatsPanel,
  useQueryMetrics,
  useStats,
} from '@dxos/devtools';
import { Filter, Obj, Query, Type } from '@dxos/echo';
import { log } from '@dxos/log';
import { type PublicKey, useClient } from '@dxos/react-client';
import { type Space, useQuery, useSpaces } from '@dxos/react-client/echo';
import * as Hooks from '@dxos/react-ui/Hooks';

import { Document, Item } from '../data.ts';
import { defs } from '../defs.ts';
import { exportData, importData } from '../util/index.ts';
import { AppToolbar } from './AppToolbar.tsx';
import { DataToolbar, type DataView } from './DataToolbar.tsx';
import { ItemList } from './ItemList.tsx';
import { ItemTable } from './ItemTable.tsx';
import { SpaceToolbar } from './SpaceToolbar.tsx';
import { StatusBar } from './status/index.ts';

export const Main = () => {
  const client = useClient();
  const spaces = useSpaces({ all: true });
  const [space, setSpace] = useState<Space>();
  useEffect(() => {
    if (!space && spaces.length) {
      setSpace(spaces[0]);
    }
  }, [spaces.length]);

  const [showDevTools, setShowDevTools] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [stats, refreshStats] = useStats();
  const { queries } = useQueryMetrics();

  const [view, setView] = useState<DataView>();
  const [type, setType] = useState<string>();
  const [filter, setFilter] = useState<string>();
  const [flushing, setFlushing] = useState(false);
  const flushingPromise = useRef<Promise<void>>(null);
  const download = Hooks.useFileDownload();

  // TODO(burdon): [BUG]: Shows deleted objects.
  // TODO(burdon): Remove restricted list of objects.
  const typeMap = useMemo(
    () =>
      [Item, Document].reduce((map, type) => {
        map.set(Type.getTypename(type), type);
        return map;
      }, new Map<string, typeof Item | typeof Document>()),
    [],
  );

  const getType = (typename: string | undefined) => typeMap.get(typename ?? Type.getTypename(Item)) ?? Item;
  const objectsOfType = useQuery(space?.db, Query.type(getType(type)));
  const objects = useMemo(
    () => objectsOfType.filter((object) => match(filter, object.content)),
    [objectsOfType, filter],
  );

  const identity = client.halo.identity.get();

  // Handle invitation.
  Hooks.useAsyncEffect(async () => {
    const url = new URL(window.location.href);
    const invitationCode = url.searchParams.get('spaceInvitationCode');
    if (invitationCode && identity) {
      const { space } = await client.shell.joinSpace({ invitationCode });
      setSpace(space);

      url.searchParams.delete('spaceInvitationCode');
      history.replaceState({}, document.title, url.href);
    }
  }, [identity]);

  const handleObjectCreate = (n = 1) => {
    if (!space) {
      return;
    }

    // TODO(burdon): Migrate generator from DebugPlugin.
    Array.from({ length: n }).forEach(() => {
      let object: Obj.Unknown;
      switch (type) {
        case Type.getTypename(Document): {
          object = Obj.make(Document, {
            title: randWord(),
            content: randSentence(),
          });
          break;
        }

        case Type.getTypename(Item):
        default: {
          object = Obj.make(Item, {
            content: randSentence(),
            // due: randBetweenDate(dateRange)
          });
          break;
        }
      }

      space.db.add(object);
    });

    setFlushing(true);
    const promise = space.db.flush();
    flushingPromise.current = promise;
    promise.then(
      () => {
        if (flushingPromise.current === promise) {
          setFlushing(false);
        }
      },
      (err) => {
        log.catch(err);
      },
    );
  };

  const handleObjectDelete = (id: string) => {
    if (!space) {
      return;
    }

    // TODO(burdon): [API]: Rename delete and just provide ID?
    const object = space.db.query(Filter.id(id)).runSync()[0];
    if (object) {
      space.db.remove(object);
    }
  };

  const handleSpaceCreate = async () => {
    const space = await client.spaces.create();
    setSpace(space);
  };

  const handleSpaceImport = async (backup: Blob) => {
    // Validate backup.
    try {
      const backupString = await backup.text();
      JSON.parse(backupString);
    } catch (err) {
      log.catch(err);
    }

    const space = await client.spaces.create();
    await space.waitUntilReady();
    await importData(space, backup);
  };

  const handleSpaceSelect = (spaceKey?: PublicKey) => {
    const space = spaceKey ? client.spaces.get(spaceKey) : undefined;
    setSpace(space);
  };

  const handleSpaceToggleOpen = async (spaceKey: PublicKey) => {
    const space = client.spaces.get(spaceKey);
    if (space) {
      await (space.isOpen ? space.close() : space.open());
    }
  };

  const handleSpaceExport = async (spaceKey: PublicKey) => {
    const space = client.spaces.get(spaceKey);
    if (space) {
      await space.waitUntilReady();
      const backupBlob = await exportData(space);
      const filename = space.properties.name?.replace(/\W/g, '_') || space.key.toHex();
      download(backupBlob, `${filename}.json`);
    }
  };

  const handleSpaceInvite = (spaceKey: PublicKey) => {
    const space = client.spaces.get(spaceKey);
    if (!space) {
      return;
    }

    void client.shell.shareSpace({ spaceKey });
  };

  return (
    <div className='flex flex-row grow justify-center overflow-hidden'>
      <div className='flex flex-col grow dx-base-surface'>
        <AppToolbar
          onHome={() => window.open(defs.issueUrl, 'DXOS')}
          onProfile={() => {
            void client.shell.open();
          }}
          onDevtools={() => setShowDevTools((showDevTools) => !showDevTools)}
        />
        <SpaceToolbar
          spaces={spaces}
          selected={space?.key ?? spaces[0]?.key}
          onCreate={handleSpaceCreate}
          onSelect={handleSpaceSelect}
          onToggleOpen={handleSpaceToggleOpen}
          onInvite={handleSpaceInvite}
          onImport={handleSpaceImport}
          onExport={handleSpaceExport}
        />
        <div className='flex flex-col grow overflow-hidden'>
          <DataToolbar
            types={Array.from(typeMap.keys())}
            onAdd={handleObjectCreate}
            onTypeChange={(type) => setType(type)}
            onFilterChange={setFilter}
            onViewChange={(view) => setView(view)}
          />

          {view === 'table' && <ItemTable type={getType(type)} objects={objects} />}
          {view === 'list' && <ItemList objects={objects} onDelete={handleObjectDelete} />}
          {view === 'debug' && <ItemList debug objects={objects} onDelete={handleObjectDelete} />}
        </div>
        <div className='flex h-[32px] p-2 items-center relative text-xs'>
          <div>{objects.length} objects</div>
          <div className='grow' />
          <StatusBar flushing={flushing} showStats={showStats} onShowStats={(show) => setShowStats(show)} />
          {showStats && (
            <div className='z-20 absolute right-0 bottom-[32px] w-[450px] border-l border-t border-neutral-500 dark:border-neutral-800'>
              <StatsPanel onRefresh={refreshStats}>
                <MemoryCard memory={stats.memory} />
                <NetworkCard network={stats.network} />
                <PerformanceCard entries={stats.performanceEntries} />
                <DatabaseCard database={stats.database} />
                <ReplicatorCard database={stats.database} />
                <ReplicatorMessagesCard database={stats.database} />
                <QueriesCard queries={queries} />
              </StatsPanel>
            </div>
          )}
        </div>
      </div>
      {showDevTools && (
        <div className='flex flex-col w-[120rem] h-full bg-white dark:bg-black'>
          <Suspense fallback={<></>}>
            <Devtools noRouter />
          </Suspense>
        </div>
      )}
    </div>
  );
};

const match = (filter: string | undefined, text: string | undefined) => {
  if (!filter?.length) {
    return true;
  }

  if (!text?.length) {
    return false;
  }

  const content = text.toLowerCase();
  const words = filter.split(/\s+/).map((word: string) => word.toLowerCase());
  return !words.some((word) => content.indexOf(word) === -1);
};
